import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  getOwnActiveOrganisation,
  getOwnActiveOrganisationAllowanceUsage,
  searchActiveOrganisationUsers,
  listInvitations,
  createInvitation,
  resendInvitation,
  revokeInvitation,
  updateInvitationRoles,
  removeUserFromActiveOrganisation,
  setActiveOrganisationUserIsActive,
  adminSwitchOrganisation,
  createOwnOrganisation,
  leaveOwnOrganisation,
  userSwitchOrganisation,
  switchToOrganisation,
  updateOwnActiveOrganisation,
  type ActiveOrganisationUpdate,
  type ActiveOrganisationInDb,
  type OrganisationSelfServiceCreate,
  type OrganisationInvitationInDb,
  type SearchQuery,
  type UserInDb,
  type UserInDbMe,
} from '@/api';
import { getWsClient } from '@/services/WebSocketService';
import { useAuthStore } from '@/stores/auth';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useIntegrationsStore } from '@/stores/integrationsStore';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { extractErrorCode, extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

export const useOrganizationStore = defineStore('organization', () => {
  // 1. State
  const users = ref<UserInDb[]>([]);
  const invitations = ref<OrganisationInvitationInDb[]>([]);
  const invitationsLoading = ref<boolean>(false);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const activeOrganization = ref<ActiveOrganisationInDb | null>(null);
  const adminOrganizationUid = ref<string | null>(null);
  const organizationAllowanceUsage = ref<number | null>(null);

  // 2. Getters

  /**
   * Every organisation change closes the socket up front (it is bound to the
   * active organisation) and normally ends in a reload onto home, which
   * reconnects. Paths that end without one — a failed switch, leaving an
   * inactive organisation — reconnect here instead.
   */
  function reconnectWebsocket(): void {
    void getWsClient().openConnection();
  }

  // 3. Actions
  /**
   * Fetches users from the current organization
   */
  async function fetchOrganizationUsers(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const query: SearchQuery = {
        text: '',
        limit: 50,
        offset: 0,
        sort_key: 'email',
        sort_direction: 'asc',
        similarity_threshold: 0.5,
      };
      const apiResponse = await searchActiveOrganisationUsers({ body: query });
      if (apiResponse.error) {
        throw new Error(
          extractErrorDetail(apiResponse.error, 'Failed to fetch organization users')
        );
      }
      const response = apiResponse.data;
      users.value = response.results;
    } catch (error_: unknown) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to fetch organization users';
      logger.error('Error fetching organization users:', error_);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetches the current active organization
   */
  async function fetchActiveOrganization(): Promise<ActiveOrganisationInDb | null> {
    loading.value = true;
    error.value = null;

    try {
      const { data: response } = await getOwnActiveOrganisation({ throwOnError: true });
      activeOrganization.value = response;

      // If this is an admin organization, store its UID for potential switching back later
      if (response.is_admin) {
        adminOrganizationUid.value = response.uid;
      }

      // Fetch the organization's allowance usage using the method for the active org
      await fetchOwnActiveOrganizationAllowanceUsage();

      return response;
    } catch (error_: unknown) {
      error.value =
        error_ instanceof Error ? error_.message : 'Failed to fetch active organization';
      logger.error('Error fetching active organization:', error_);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Changes the active organisation's settings (org admins): name, description,
   * default signing expiry and default signing provider. Omitted fields stay as
   * they are; for the provider, null clears it. A provider the organisation
   * can't use is refused with the backend's message.
   * The account menu reads names from the signed-in user's memberships, so
   * those are patched too — otherwise the switcher shows the old name.
   */
  async function updateActiveOrganization(update: ActiveOrganisationUpdate): Promise<void> {
    const response = await updateOwnActiveOrganisation({ body: update });
    if (response.error) {
      throw new Error(extractErrorDetail(response.error, 'Failed to update the organisation.'));
    }
    const organisation = response.data;
    activeOrganization.value = organisation;

    const authStore = useAuthStore();
    const me = authStore.backendUser;
    if (!me) return;
    const rename = <T extends { uid: string }>(entry: T): T =>
      entry.uid === organisation.uid
        ? { ...entry, name: organisation.name, description: organisation.description }
        : entry;
    authStore.backendUser = {
      ...me,
      organisations: me.organisations.map(entry => rename(entry)),
      active_organisation: me.active_organisation && rename(me.active_organisation),
    };
  }

  /**
   * Fetches the current user's active organization's allowance usage
   * This method is suitable for any user to check their organization's usage
   *
   * @returns The organization's credit usage as a number
   */
  async function fetchOwnActiveOrganizationAllowanceUsage(): Promise<number | null> {
    try {
      const { data: response } = await getOwnActiveOrganisationAllowanceUsage({
        throwOnError: true,
      });

      // If the response is NaN or negative, treat it as 0
      if (response === null || isNaN(response) || response < 0) {
        organizationAllowanceUsage.value = 0;
        return 0;
      } else {
        // Make sure we're storing a number, not a string
        const numericValue = typeof response === 'string' ? parseFloat(response) : response;
        organizationAllowanceUsage.value = numericValue;
        return numericValue;
      }
    } catch (error_: unknown) {
      logger.error('Error fetching own organization allowance usage:', error_);
      organizationAllowanceUsage.value = 0; // Default to 0 on error
      return 0;
    }
  }

  /**
   * Fetches the current organization's invitations
   */
  async function fetchInvitations(): Promise<void> {
    invitationsLoading.value = true;
    error.value = null;

    try {
      const { data, error: apiError } = await listInvitations({ throwOnError: false });
      if (apiError) {
        throw new Error(extractErrorDetail(apiError, 'Failed to fetch invitations'));
      }
      invitations.value = data;
    } catch (error_: unknown) {
      error.value = extractErrorDetail(error_, 'Failed to fetch invitations');
      logger.error('Error fetching invitations:', error_);
    } finally {
      invitationsLoading.value = false;
    }
  }

  /**
   * Invites a user to the current organization. The backend sends the
   * invitation mail; the invitee accepts via the /invite/accept page.
   */
  async function sendInvitation(
    userEmail: string,
    fullName: string,
    roleUids: string[] = []
  ): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const { error: apiError } = await createInvitation({
        body: { email: userEmail, full_name: fullName, permission_role_uids: roleUids },
        throwOnError: false,
      });
      if (apiError) {
        throw new Error(extractErrorDetail(apiError, 'Failed to send invitation'));
      }

      await fetchInvitations();
      return true;
    } catch (error_: unknown) {
      // The 409 conflicts arrive as plain-string details — map them to
      // actionable copy instead of surfacing the backend phrasing.
      const detail = extractErrorDetail(error_, 'Failed to send invitation');
      if (detail.includes('pending invitation')) {
        error.value = 'An invitation for this email is already pending — use Resend instead.';
      } else if (detail.includes('organisation member')) {
        error.value = 'This email already belongs to a member of your organisation.';
      } else {
        error.value = detail;
      }
      logger.error('Error sending invitation:', error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Resends an invitation mail (rotates the token and refreshes the expiry)
   */
  async function resendOrganisationInvitation(uid: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const { error: apiError } = await resendInvitation({ path: { uid }, throwOnError: false });
      if (apiError) {
        throw new Error(extractErrorDetail(apiError, 'Failed to resend invitation'));
      }

      await fetchInvitations();
      return true;
    } catch (error_: unknown) {
      error.value = extractErrorDetail(error_, 'Failed to resend invitation');
      logger.error('Error resending invitation:', error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Revokes a pending invitation
   */
  async function revokeOrganisationInvitation(uid: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const { error: apiError } = await revokeInvitation({ path: { uid }, throwOnError: false });
      if (apiError) {
        throw new Error(extractErrorDetail(apiError, 'Failed to revoke invitation'));
      }

      await fetchInvitations();
      return true;
    } catch (error_: unknown) {
      error.value = extractErrorDetail(error_, 'Failed to revoke invitation');
      logger.error('Error revoking invitation:', error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Replaces the role set of a pending invitation (empty array clears it)
   */
  async function updateInvitationRoleSet(uid: string, roleUids: string[]): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const { error: apiError } = await updateInvitationRoles({
        path: { uid },
        body: { permission_role_uids: roleUids },
        throwOnError: false,
      });
      if (apiError) {
        throw new Error(extractErrorDetail(apiError, 'Failed to update invitation roles'));
      }

      await fetchInvitations();
      return true;
    } catch (error_: unknown) {
      error.value = extractErrorDetail(error_, 'Failed to update invitation roles');
      logger.error('Error updating invitation roles:', error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Activates or deactivates a user account. Deactivation is platform-wide,
   * not per-organisation — the flag lives on the user itself.
   */
  async function setUserActiveState(uid: string, isActive: boolean): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const { data, error: apiError } = await setActiveOrganisationUserIsActive({
        path: { uid },
        body: { is_active: isActive },
        throwOnError: false,
      });
      if (apiError) {
        throw new Error(
          extractErrorDetail(
            apiError,
            isActive ? 'Failed to activate user' : 'Failed to deactivate user'
          )
        );
      }

      const index = users.value.findIndex(user => user.uid === uid);
      if (index !== -1) {
        users.value[index] = data;
      }
      return true;
    } catch (error_: unknown) {
      error.value = extractErrorDetail(
        error_,
        isActive ? 'Failed to activate user' : 'Failed to deactivate user'
      );
      logger.error('Error setting user active state:', error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Removes a user from the current active organization
   */
  async function removeUserFromOrganization(uid: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const apiResponse = await removeUserFromActiveOrganisation({ path: { uid } });
      if (apiResponse.error) {
        throw new Error(extractErrorDetail(apiResponse.error, 'Failed to remove user'));
      }

      await fetchOrganizationUsers();
      return true;
    } catch (error_: unknown) {
      // Surface backend detail (e.g. "Organisation must keep at least one
      // admin") rather than a generic message — the API returns it as a
      // { detail: ... } body, which is not an Error instance.
      error.value = extractErrorDetail(error_, 'Failed to remove user');
      logger.error('Error removing user from organization:', error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Leaves an organisation the current user is a member of — the active one
   * or any other. The backend cuts the membership, roles and active pointer
   * in one go (falling back to the next organisation, or to none) and answers
   * with the caller's refreshed profile, which goes straight into the auth
   * store so the switcher, home and the profile's list agree without a refetch.
   *
   * Everything else the app has cached is scoped to the old organisation, so a
   * caller that left the *active* one should still reload onto home.
   *
   * @param uid - The UID of the organisation to leave
   * @returns The refreshed profile, or null on failure (message in `error`)
   */
  async function leaveOrganization(uid: string): Promise<UserInDbMe | null> {
    loading.value = true;
    error.value = null;

    const leavingActive = useAuthStore().backendUser?.active_organisation?.uid === uid;
    try {
      // Leaving the active organisation moves the active pointer, which the
      // socket is bound to. Close it first, so this tab doesn't take the
      // server's 4001 for a change made elsewhere.
      getWsClient().disconnectForOrgChange();
      const apiResponse = await leaveOwnOrganisation({ path: { organisation_uid: uid } });
      if (apiResponse.error) {
        throw new Error(extractErrorDetail(apiResponse.error, 'Failed to leave organisation'));
      }

      useAuthStore().backendUser = apiResponse.data;
      if (activeOrganization.value?.uid === uid) {
        activeOrganization.value = null;
        organizationAllowanceUsage.value = null;
      }
      // Still in the same organisation and no reload coming: back online.
      if (!leavingActive) reconnectWebsocket();
      return apiResponse.data;
    } catch (error_: unknown) {
      // The last-admin refusal arrives as backend detail worth showing as-is.
      error.value = extractErrorDetail(error_, 'Failed to leave organisation');
      logger.error('Error leaving organisation:', error_);
      reconnectWebsocket();
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Switches to a different organization
   *
   * @param uid - The UID of the organization to switch to
   * @returns True if successful, false otherwise
   */
  async function switchToOrganization(uid: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      // Use switchOrganisation to switch organizations
      getWsClient().disconnectForOrgChange();
      useGaiaStore().clearAll();
      useIntegrationsStore().clearAll();
      useContractTypeStore().clearAll();
      const apiResponse = await adminSwitchOrganisation({ query: { organisation_uid: uid } });
      if (apiResponse.error) {
        throw new Error(extractErrorDetail(apiResponse.error, 'Failed to switch organization'));
      }
      const response = apiResponse.data;

      // Update the active organization in the store
      activeOrganization.value = response;

      // Fetch the organization's allowance usage
      await fetchOwnActiveOrganizationAllowanceUsage();

      return true;
    } catch (error_: unknown) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to switch organization';
      logger.error('Error switching organization:', error_);
      reconnectWebsocket();
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Switches the current user to one of their member organizations
   *
   * @param uid - The UID of the organization to switch to
   * @returns True if successful, false otherwise
   */
  async function switchCurrentUserOrganization(uid: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      getWsClient().disconnectForOrgChange();
      useGaiaStore().clearAll();
      useIntegrationsStore().clearAll();
      const apiResponse = await userSwitchOrganisation({ query: { organisation_uid: uid } });
      if (apiResponse.error) {
        throw new Error(extractErrorDetail(apiResponse.error, 'Failed to switch organization'));
      }
      const response = apiResponse.data;

      activeOrganization.value = response;
      await fetchOwnActiveOrganizationAllowanceUsage();

      return true;
    } catch (error_: unknown) {
      error.value = error_ instanceof Error ? error_.message : 'Failed to switch organization';
      logger.error('Error switching current user organization:', error_);
      reconnectWebsocket();
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Creates an organisation for the current user (self-service, behind the
   * backend's SELF_SERVICE_ORGANISATION_CREATION flag). The backend makes the
   * caller its admin and switches their active organisation to it, so this is
   * both the way out of the org-less state and a plain "new workspace" action.
   * Like a switch, the caller is expected to reload onto home afterwards.
   *
   * @param body - Name, optional description and hosting zone
   * @returns True if successful, false otherwise (the message is in `error`)
   */
  async function createOwnOrganization(body: OrganisationSelfServiceCreate): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      getWsClient().disconnectForOrgChange();
      useGaiaStore().clearAll();
      useIntegrationsStore().clearAll();
      const apiResponse = await createOwnOrganisation({ body });
      if (apiResponse.error) {
        throw new Error(extractErrorDetail(apiResponse.error, 'Failed to create organisation'));
      }

      activeOrganization.value = apiResponse.data;
      return true;
    } catch (error_: unknown) {
      // The flag-off 403 is a coded error; the code is the stable thing to
      // match on, so the message can be ours rather than the backend's.
      error.value =
        extractErrorCode(error_) === 'self_service_organisation_creation_disabled'
          ? 'Creating organisations is disabled on this deployment.'
          : extractErrorDetail(error_, 'Failed to create organisation');
      logger.error('Error creating organisation:', error_);
      reconnectWebsocket();
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Switches back to the admin organization
   * This is a specialized method for admin users to switch back to their admin organization
   *
   * @returns True if successful, false otherwise
   */
  async function switchToAdminOrganization(): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      // Use switchToOrganisation to switch to admin organization
      getWsClient().disconnectForOrgChange();
      useGaiaStore().clearAll();
      useIntegrationsStore().clearAll();
      const { data: response } = await switchToOrganisation({ throwOnError: true });

      // Update the active organization in the store
      activeOrganization.value = response;
      adminOrganizationUid.value = response.uid;

      // Fetch the organization's allowance usage
      await fetchOwnActiveOrganizationAllowanceUsage();

      return true;
    } catch (error_: unknown) {
      error.value =
        error_ instanceof Error ? error_.message : 'Failed to switch to admin organization';
      logger.error('Error switching to admin organization:', error_);
      reconnectWebsocket();
      return false;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    users,
    invitations,
    invitationsLoading,
    loading,
    error,
    activeOrganization,
    updateActiveOrganization,
    organizationAllowanceUsage,

    // Actions
    fetchOrganizationUsers,
    fetchActiveOrganization,
    fetchOwnActiveOrganizationAllowanceUsage,
    fetchInvitations,
    sendInvitation,
    resendOrganisationInvitation,
    revokeOrganisationInvitation,
    updateInvitationRoleSet,
    setUserActiveState,
    removeUserFromOrganization,
    leaveOrganization,
    switchToOrganization,
    switchCurrentUserOrganization,
    switchToAdminOrganization,
    createOwnOrganization,
  };
});
